import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import { auth, dbAccounts } from "../../../../backend/firebaseConfig";
import { assertChargeDatesAreOpen } from "../utils/closedChargeMonths";

const payments = collection(dbAccounts, "MonthlyManualPayments");

export const subscribeMonthlyManualPayments = (onData, onError) =>
  onSnapshot(
    payments,
    (snapshot) => onData(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
    onError,
  );

export const getMonthlyManualPayments = async () => {
  const snapshot = await getDocs(payments);
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
};

export const createManualPayment = async ({
  adviserEmail,
  adviserName,
  adviserId = "",
  brandName = "",
  paymentType,
  billingType = "one_time",
  date,
  paymentTo = "",
  description = "",
  amount,
  notes = "",
}) => {
  const amountPaid = Number(amount);
  const recurring = billingType === "recurring";
  if (!adviserEmail) throw new Error("An adviser email is required.");
  if (!date) throw new Error("A payment date is required.");
  if (!Number.isFinite(amountPaid) || amountPaid <= 0) {
    throw new Error("Enter a valid payment amount.");
  }
  if (recurring && !paymentTo) {
    throw new Error("A recurring payment end date is required.");
  }
  if (paymentTo && paymentTo < date) {
    throw new Error("The payment end date must be after the payment start date.");
  }
  await assertChargeDatesAreOpen([date, paymentTo]);
  const user = auth.currentUser;
  await addDoc(payments, {
    recordType: "manual_payment",
    paymentType,
    billingType: recurring ? "recurring" : "one_time",
    adviserEmail: String(adviserEmail).trim().toLowerCase(),
    adviserName: String(adviserName || "").trim(),
    adviserId,
    brandName,
    date: new Date(`${date}T00:00:00.000Z`).toISOString(),
    paymentMonth: String(date).slice(0, 7),
    paymentFrom: new Date(`${date}T00:00:00.000Z`).toISOString(),
    paymentTo:
      recurring && paymentTo
        ? new Date(`${paymentTo}T00:00:00.000Z`).toISOString()
        : null,
    description: String(description || "").trim(),
    amountPaid,
    amountDeducted: paymentType === "advance" ? amountPaid : 0,
    netAmount: paymentType === "advance" ? 0 : amountPaid,
    deductionMonth:
      paymentType === "advance" ? String(date).slice(0, 7) : null,
    notes: String(notes || "").trim(),
    createdAt: serverTimestamp(),
    createdBy: {
      uid: user?.uid || "",
      email: user?.email || "",
      name: user?.displayName || user?.email || "",
    },
  });
};

export const updateManualPayment = async ({ paymentId, ...input }) => {
  if (!paymentId) throw new Error("A payment ID is required.");
  const amountPaid = Number(input.amount);
  const recurring = input.billingType === "recurring";
  if (!input.date) throw new Error("A payment date is required.");
  if (!Number.isFinite(amountPaid) || amountPaid <= 0) throw new Error("Enter a valid payment amount.");
  if (recurring && !input.paymentTo) {
    throw new Error("A recurring payment end date is required.");
  }
  if (input.paymentTo && input.paymentTo < input.date) {
    throw new Error("The payment end date must be after the payment start date.");
  }
  const paymentRef = doc(dbAccounts, "MonthlyManualPayments", paymentId);
  const paymentSnapshot = await getDoc(paymentRef);
  if (!paymentSnapshot.exists()) throw new Error("Payment not found.");
  await assertChargeDatesAreOpen([
    paymentSnapshot.data().date,
    paymentSnapshot.data().paymentTo,
    input.date,
    input.paymentTo,
  ]);
  const paymentMonth = String(input.date).slice(0, 7);
  const isAdvance = input.paymentType === "advance";
  await updateDoc(paymentRef, {
    paymentType: input.paymentType,
    billingType: recurring ? "recurring" : "one_time",
    date: new Date(`${input.date}T00:00:00.000Z`).toISOString(),
    paymentMonth,
    paymentFrom: new Date(`${input.date}T00:00:00.000Z`).toISOString(),
    paymentTo:
      recurring && input.paymentTo
        ? new Date(`${input.paymentTo}T00:00:00.000Z`).toISOString()
        : null,
    description: String(input.description || "").trim(),
    amountPaid,
    amountDeducted: isAdvance ? amountPaid : 0,
    netAmount: isAdvance ? 0 : amountPaid,
    deductionMonth: isAdvance ? paymentMonth : null,
    notes: String(input.notes || "").trim(),
    updatedAt: serverTimestamp(),
  });
};

export const deleteManualPayment = async (paymentId) => {
  if (!paymentId) throw new Error("A payment ID is required.");
  const paymentRef = doc(dbAccounts, "MonthlyManualPayments", paymentId);
  const paymentSnapshot = await getDoc(paymentRef);
  if (!paymentSnapshot.exists()) throw new Error("Payment not found.");
  await assertChargeDatesAreOpen([paymentSnapshot.data().date]);
  return deleteDoc(paymentRef);
};
